import { useEffect, useMemo, useState } from 'react';
import {
  seriesAPI, divisionsAPI, groundsAPI, fixturesAPI, conditionsAPI
} from '../../services/api';
import { PageHeader, Spinner, EmptyState } from '../../components/ui';
import DateRangePicker from '../../components/DateRangePicker';
import toast from 'react-hot-toast';

// Ground Availability report: for each ground, count of still-free slots per
// month (available date × supported start times, minus scheduled fixtures).
// Rows = grounds, columns = season months, cells = free slot count.

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DEFAULT_TIMES = ['08:45:00', '12:30:00', '16:00:00'];

function parseAllowedTimes(raw) {
  if (!raw) return null;
  const out = [];
  String(raw).split(/[,;]/).forEach(part => {
    const s = part.trim().toUpperCase();
    const m = s.match(/^(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM)?$/);
    if (!m) return;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2] || '0', 10);
    const ap = m[3];
    if (ap === 'PM' && h < 12) h += 12;
    if (ap === 'AM' && h === 12) h = 0;
    out.push(`${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}:00`);
  });
  return out.length ? out : null;
}

function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function normTime(t) {
  if (!t) return null;
  const s = String(t);
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  return s;
}

export default function GroundAvailabilityPage() {
  const [seriesList, setSeriesList] = useState([]);
  const [seriesId, setSeriesId] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [availability, setAvailability] = useState([]);  // ground_availability rows
  const [fixtures, setFixtures] = useState([]);
  const [groundRules, setGroundRules] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [divisionId, setDivisionId] = useState('');
  const [months, setMonths] = useState([]);      // int 0-11; empty = all season
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await seriesAPI.list();
        setSeriesList(data);
        if (data.length && !seriesId) {
          const active = data.find(s => s.status === 'active') || data[0];
          setSeriesId(active.id);
        }
      } catch (e) { toast.error('Failed to load series'); }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!seriesId) return;
    (async () => {
      setLoading(true);
      try {
        const [{ data: divs }, { data: gs }, { data: av }, { data: fs }, { data: conds }] = await Promise.all([
          divisionsAPI.list(seriesId),
          groundsAPI.list(),
          groundsAPI.getAllAvailability(seriesId),
          fixturesAPI.list({ series_id: seriesId }),
          conditionsAPI.list({ series_id: seriesId }),
        ]);
        setDivisions(divs);
        setGrounds(gs);
        setAvailability(av);
        setFixtures(fs);
        const gr = (conds || []).filter(c => c.condition_type === 'ground_rule').map(c => ({
          ground_id: c.value?.ground_id,
          allowed_times: c.value?.allowed_times || '',
          premier_priority: !!c.value?.premier_priority,
          allowed_division_ids: Array.isArray(c.value?.allowed_division_ids) ? c.value.allowed_division_ids : [],
          division_restriction: c.value?.division_restriction || 'none',
        })).filter(r => r.ground_id);
        setGroundRules(gr);
        setDivisionId('');
        setMonths([]);
        setDateRange({ start: null, end: null });
      } catch (e) { toast.error('Load failed'); }
      finally { setLoading(false); }
    })();
  }, [seriesId]);

  // Map ground_id → allowed start times (array of HH:MM:SS). Falls back to default.
  const groundTimesById = useMemo(() => {
    const m = {};
    grounds.forEach(g => { m[g.id] = DEFAULT_TIMES; });
    groundRules.forEach(r => {
      const parsed = parseAllowedTimes(r.allowed_times);
      if (parsed) m[r.ground_id] = parsed;
    });
    return m;
  }, [grounds, groundRules]);

  const groundRuleById = useMemo(() => {
    const m = {};
    groundRules.forEach(r => { m[r.ground_id] = r; });
    return m;
  }, [groundRules]);

  // Which grounds can the selected division use (mirrors scheduler's canUseGround
  // minus the series-policy piece — good enough for this report).
  const canDivisionUseGround = (divId, ground) => {
    if (!divId) return true; // "all divisions" — show everything
    const div = divisions.find(d => d.id === divId);
    const divName = (div?.name || '').toLowerCase();
    const isPremier = divName === 'premier';
    const rule = groundRuleById[ground.id];
    const isPremierGround = ground.ground_type === 'premier';
    if (isPremier) return isPremierGround;
    if (!isPremierGround) return true;
    // Non-Premier at a premier ground — check rule restrictions
    if (rule?.division_restriction === 'premier_only') return false;
    if (rule?.premier_priority && rule.allowed_division_ids?.length > 0) {
      return rule.allowed_division_ids.includes(divId);
    }
    return true;
  };

  // Season months: every month between the earliest and latest date across
  // availability rows (any status) and fixtures. Falls back to any months
  // that appear explicitly. Ensures gaps like "no availability set for July
  // yet" don't drop July from the filter.
  const seasonMonths = useMemo(() => {
    const dates = [];
    availability.forEach(a => {
      const d = String(a.available_date || '').split('T')[0];
      if (d) dates.push(d);
    });
    fixtures.forEach(f => {
      const d = String(f.match_date || '').split('T')[0];
      if (d) dates.push(d);
    });
    const s = seriesList.find(x => x.id === seriesId);
    if (s?.start_date) dates.push(String(s.start_date).split('T')[0]);
    if (s?.end_date)   dates.push(String(s.end_date).split('T')[0]);
    if (!dates.length) return [];
    dates.sort();
    const minYM = dates[0].substring(0, 7);
    const maxYM = dates[dates.length - 1].substring(0, 7);
    const [minY, minM] = minYM.split('-').map(Number);
    const [maxY, maxM] = maxYM.split('-').map(Number);
    const months = new Set();
    let y = minY, m = minM;
    while (y < maxY || (y === maxY && m <= maxM)) {
      months.add(m - 1);
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    return [...months].sort((a, b) => a - b);
  }, [availability, fixtures, seriesList, seriesId]);

  // Build slot usage set from fixtures: "date|ground_id|HH:MM:SS"
  const usedSlotSet = useMemo(() => {
    const s = new Set();
    fixtures.forEach(f => {
      if (f.status !== 'confirmed') return;
      if (!f.match_date || !f.ground_id || !f.match_time) return;
      const d = String(f.match_date).split('T')[0];
      const t = normTime(f.match_time);
      s.add(`${d}|${f.ground_id}|${t}`);
    });
    return s;
  }, [fixtures]);

  // Per-ground, per-month free-slot count
  const stats = useMemo(() => {
    // { ground_id → { month → free_count, _total } }
    const out = {};
    const startStr = dateRange.start ? toYMD(dateRange.start) : null;
    const endStr   = dateRange.end   ? toYMD(dateRange.end)   : null;
    const monthSet = months.length ? new Set(months) : null;

    const eligibleGrounds = grounds.filter(g => canDivisionUseGround(divisionId, g));

    eligibleGrounds.forEach(g => { out[g.id] = { _total: 0 }; });

    availability.forEach(a => {
      if (a.status !== 'available') return;
      const d = String(a.available_date || '').split('T')[0];
      if (!d) return;
      if (startStr && d < startStr) return;
      if (endStr && d > endStr) return;
      const m = parseInt(d.substring(5, 7), 10) - 1;
      if (monthSet && !monthSet.has(m)) return;
      const gid = a.ground_id;
      if (!out[gid]) return; // ground not eligible for the division filter
      const times = groundTimesById[gid] || DEFAULT_TIMES;
      for (const t of times) {
        const sk = `${d}|${gid}|${t}`;
        if (usedSlotSet.has(sk)) continue;
        out[gid][m] = (out[gid][m] || 0) + 1;
        out[gid]._total += 1;
      }
    });
    return out;
  }, [availability, fixtures, grounds, divisions, divisionId, months, dateRange, groundTimesById, usedSlotSet]); // eslint-disable-line

  // Active season months after filter (narrower view if user limited via date range)
  const displayMonths = useMemo(() => {
    if (months.length) return [...months].sort((a, b) => a - b);
    return seasonMonths;
  }, [months, seasonMonths]);

  // Ground rows: only those eligible to the division filter, sorted alpha
  const rowGrounds = useMemo(() => {
    return grounds
      .filter(g => canDivisionUseGround(divisionId, g))
      .filter(g => stats[g.id]) // have stats
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [grounds, stats, divisionId, divisions, groundRuleById]); // eslint-disable-line

  const toggleInArray = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
  const totalFree = Object.values(stats).reduce((s, row) => s + (row._total || 0), 0);

  return (
    <div>
      <PageHeader
        icon="◷"
        title="Ground Availability"
        subtitle="Free slots remaining per ground, per month — after the schedule is in"
      />

      {/* Filter bar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        padding: '12px 14px', background: 'var(--card)', borderRadius: 8,
        border: '1px solid var(--border)', marginBottom: 14,
      }}>
        <FilterField label="Series">
          <select className="input" value={seriesId} onChange={e => setSeriesId(e.target.value)} style={{ fontSize: 12 }}>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </FilterField>

        <FilterField label="Division">
          <select className="input" value={divisionId} onChange={e => setDivisionId(e.target.value)} style={{ fontSize: 12 }}>
            <option value="">All divisions</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </FilterField>

        <FilterField label="Date range">
          <DateRangePicker
            start={dateRange.start}
            end={dateRange.end}
            onChange={setDateRange}
            size="sm"
          />
        </FilterField>

        <FilterField label="Months">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {seasonMonths.length === 0 ? (
              <span style={{ fontSize: 10, color: 'var(--dim)' }}>No season data</span>
            ) : seasonMonths.map(i => {
              const on = months.includes(i);
              return (
                <button key={i} type="button"
                  onClick={() => setMonths(toggleInArray(months, i))}
                  style={{
                    fontSize: 10, padding: '3px 7px', borderRadius: 4,
                    background: on ? 'var(--accent)' : 'var(--surface)',
                    color: on ? 'white' : 'var(--mid)',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                    cursor: 'pointer',
                  }}>{MONTH_NAMES[i]}</button>
              );
            })}
          </div>
        </FilterField>

        {(divisionId || months.length || dateRange.start || dateRange.end) && (
          <button
            type="button"
            onClick={() => { setDivisionId(''); setMonths([]); setDateRange({ start: null, end: null }); }}
            style={{
              fontSize: 11, padding: '5px 10px', borderRadius: 5,
              background: 'var(--surface)', color: 'var(--red)', border: '1px solid var(--border)',
              cursor: 'pointer',
            }}
          >Clear filters</button>
        )}
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
        <Summary label="Grounds" value={rowGrounds.length} />
        <Summary label="Months" value={displayMonths.length} />
        <Summary label="Free slots" value={totalFree} />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
      ) : !rowGrounds.length || !displayMonths.length ? (
        <EmptyState icon="◷" title="No availability data"
          description="Pick a series that has ground availability set and a generated schedule" />
      ) : (
        <AvailabilityTable
          grounds={rowGrounds}
          months={displayMonths}
          stats={stats}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────────

function AvailabilityTable({ grounds, months, stats }) {
  // color scale: high free = green, zero = dim
  let max = 0;
  grounds.forEach(g => { months.forEach(m => { max = Math.max(max, stats[g.id]?.[m] || 0); }); });
  const colorFor = (n) => {
    if (!n) return { bg: 'transparent', fg: 'var(--dim)' };
    const r = Math.min(1, n / Math.max(3, max));
    return {
      bg: `rgba(0, 200, 150, ${0.08 + r * 0.35})`,
      fg: r > 0.5 ? 'var(--accent)' : 'var(--text)',
    };
  };

  return (
    <div style={{
      overflow: 'auto', border: '1px solid var(--border)', borderRadius: 8,
      background: 'var(--card)', maxHeight: 'calc(100vh - 280px)',
    }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--surface)' }}>
            <th style={{ ...headCell, position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', minWidth: 160 }}>Ground</th>
            {months.map(m => (
              <th key={m} style={{ ...headCell, minWidth: 72, textAlign: 'center' }}>
                {MONTH_NAMES[m]}
              </th>
            ))}
            <th style={{ ...headCell, minWidth: 70, textAlign: 'center', background: 'var(--surface)' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {grounds.map(g => {
            const row = stats[g.id] || {};
            return (
              <tr key={g.id}>
                <td style={{
                  ...bodyCell, position: 'sticky', left: 0, background: 'var(--card)',
                  fontWeight: 700, color: 'var(--text)',
                  fontFamily: "'Roboto Mono', monospace",
                }}>
                  {g.name}
                </td>
                {months.map(m => {
                  const n = row[m] || 0;
                  const c = colorFor(n);
                  return (
                    <td key={m} style={{
                      ...bodyCell, textAlign: 'center',
                      background: c.bg, color: c.fg,
                      fontWeight: n ? 600 : 400,
                      fontFamily: "'Roboto Mono', monospace",
                    }}>
                      {n || '—'}
                    </td>
                  );
                })}
                <td style={{
                  ...bodyCell, textAlign: 'center', fontWeight: 700,
                  color: 'var(--accent)', background: 'var(--surface)',
                  fontFamily: "'Roboto Mono', monospace",
                }}>
                  {row._total || 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
      {children}
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div style={{
      padding: '8px 14px', background: 'var(--card)', borderRadius: 6,
      border: '1px solid var(--border)', minWidth: 90,
    }}>
      <div style={{ fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent)', fontFamily: "'Roboto Mono', monospace" }}>{value}</div>
    </div>
  );
}

const headCell = {
  padding: '8px 10px', fontWeight: 700, fontSize: 11,
  color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: 0.4,
  borderBottom: '1px solid var(--border)', textAlign: 'left',
};
const bodyCell = { padding: '6px 10px', borderBottom: '1px solid var(--border)' };
